import React, { useState } from "react";
import { Card, Empty } from "antd";
import OnBoardingFormBuilder from "./OnboardingFormBuilder";
import CandidateApprovalFormBuilder from "./CandidateApprovalFormBuilder";
import OnboardingInitiationFormBuilder from "./OnboardingInitiationFormBuilder";
import EmployeeMasterFormBuilder from "./EmployeeMasterFormBuilder";
import RejoinEmployeeFormBuilder from "./RejoinEmployeeFormBuilder";
import ExitFeedbackFormBuilder from "./ExitFeedbackFormBuilder";
import { getResource } from "../../util/getResourcePermission";
import config from "../../permission/config.json";

/**
 * Central Form Builder page. Left sub-nav lists the configurable forms; the
 * selected form's builder renders on the right. Each entry is permission-gated,
 * so a user only sees the builders they have read access to.
 */
// Plan slugs per form - mirrors the sidebar's RESOURCE_TO_SIDEBAR_SLUGS so each
// builder inherits the SuperAdmin plan access of its own module.
const ONBOARDING_PLAN_SLUGS = ["doc_master", "onboarding_master", "onboarding_initiation"];
const FORMS = [
  {
    key: "onboarding_master",
    label: "Onboarding Form Master",
    resource: config.RESOURCES.ON_BOARDING_INITIATION,
    planSlugs: ONBOARDING_PLAN_SLUGS,
    Component: OnBoardingFormBuilder,
  },
  {
    key: "candidate_approval",
    label: "Candidate Approval",
    resource: config.RESOURCES.CANDIDATE_APPROVAL,
    planSlugs: ["candidate_approval", "candidate_list"],
    Component: CandidateApprovalFormBuilder,
  },
  {
    key: "onboarding_initiation",
    label: "Onboarding Initiation",
    resource: config.RESOURCES.ON_BOARDING_INITIATION,
    planSlugs: ONBOARDING_PLAN_SLUGS,
    Component: OnboardingInitiationFormBuilder,
  },
  {
    key: "employee_master",
    label: "Employee Master",
    resource: config.RESOURCES.EMPLOYEE_MASTER,
    planSlugs: ["employee_master"],
    Component: EmployeeMasterFormBuilder,
  },
  {
    key: "rejoin_employee",
    label: "Rejoin Employee",
    resource: config.RESOURCES.EMPLOYEE_MASTER,
    planSlugs: ["employee_master"],
    Component: RejoinEmployeeFormBuilder,
  },
  {
    key: "exit_feedback",
    label: "Exit Feedback",
    resource: config.RESOURCES.SEPARATION_MANAGEMENT,
    planSlugs: ["resignation_employee_list", "last_working_day_list"],
    Component: ExitFeedbackFormBuilder,
  },
];

// SuperAdmin plan module access (empty = no plan configured → skip plan gate).
const getPlanModuleAccess = () => {
  try {
    const raw = JSON.parse(localStorage.getItem("sidebarModuleAccess") || "[]");
    return new Set(Array.isArray(raw) ? raw : []);
  } catch {
    return new Set();
  }
};

const FormBuilderHub = () => {
  const planAccess = getPlanModuleAccess();
  const isPlanEnabled = (form) =>
    planAccess.size === 0 || (form.planSlugs || []).some((slug) => planAccess.has(slug));

  // A builder shows only when the role has read permission AND the plan includes its module.
  const available = FORMS.filter(
    (f) => getResource(f.resource, ["read"]) && isPlanEnabled(f)
  );
  const [activeKey, setActiveKey] = useState(available[0]?.key);

  const activeForm =
    available.find((f) => f.key === activeKey) || available[0] || null;
  const ActiveComponent = activeForm?.Component;

  return (
    <Card className="employee-card form-builder-hub">
      <div className="inout-header text-left">
        <h2>Form Builder</h2>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {/* Left sub-nav */}
        <div
          className="form-builder-hub-nav"
          style={{
            width: 240,
            flex: "0 0 240px",
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 8,
            position: "sticky",
            top: 16,
          }}
        >
          {available.length === 0 ? (
            <div style={{ padding: 16, color: "#999" }}>No access</div>
          ) : (
            available.map((f) => {
              const isActive = activeForm?.key === f.key;
              return (
                <div
                  key={f.key}
                  onClick={() => setActiveKey(f.key)}
                  style={{
                    padding: "10px 14px",
                    marginBottom: 4,
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#03497a" : "#333",
                    background: isActive ? "#e6f4ff" : "transparent",
                  }}
                >
                  {f.label}
                </div>
              );
            })
          )}
        </div>

        {/* Selected builder */}
        <div className="form-builder-hub-content" style={{ flex: 1, minWidth: 0 }}>
          {ActiveComponent ? (
            <ActiveComponent />
          ) : (
            <Empty description="You do not have access to any form builder" />
          )}
        </div>
      </div>
    </Card>
  );
};

export default FormBuilderHub;
